import { useEffect } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { SurfaceError } from '../customer/SurfaceState.js'
import { Button } from '../ui/index.js'
import { cx } from '../ui/cx.js'
import { useTenantLogout, useTenantMe } from './queries.js'
import styles from './RequireTenantAuth.module.css'

const NAV = [
  // `SPEC-LGH-003 §7`'s own order — configuration first, because nothing else
  // in this console has real data without it. The shipped nav opened on Team.
  { to: '/customer/price-lists', label: 'Price lists' },
  { to: '/customer/commercial-rules', label: 'Commercial rules' },
  { to: '/customer/team', label: 'Team' },
]

/**
 * `LGH-040`. The customer console's own session-aware shell — a sibling of
 * `auth/RequireAuth.tsx`, not a variant of it. Deliberately thin next to the
 * operator shell: no tenant switcher (a tenant member has exactly one
 * tenant, chosen at session-creation, never selected), no degradation
 * banner or provenance line yet (`LGH-042`'s dashboard is where a customer-
 * facing health signal would eventually live, not this shell).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `DES-001`, presentation only — the three `useTenantMe` states and what each
 * one decides are unchanged.
 *
 * "Deliberately thin" had turned into "unstyled": inline styles, no ground, no
 * rule, default-blue underlined hyperlinks for the nav, no current-page state,
 * and no product identity of any kind. A customer signing in to their
 * supplier's platform saw their own name in a corner and three blue links.
 *
 * The tenant's NAME still isn't here, and can't be: `tenantAuthMeSchema`
 * carries `tenant.id` only. Filed to `3L` rather than rendering a UUID
 * (`REQ-design-the-customer-console-ux-audit`, finding S2).
 * ───────────────────────────────────────────────────────────────────────────
 */
export function RequireTenantAuth() {
  const location = useLocation()
  const query = useTenantMe()
  const { data, isPending, isError } = query
  const logout = useTenantLogout()

  useEffect(() => {
    document.title = 'Your account · Pruvato'
  }, [])

  /**
   * `REQ-design-the-customer-console-ux-audit` finding S1 — the same
   * `fetchStatus: 'paused'` hazard `ReviewPage.tsx` found and fixed
   * (`LGH-038`, 2026-09-15): React Query's `networkMode: 'online'` pauses a
   * fetch rather than failing it once it judges the network down, and
   * `status`/`isPending` never change from what they were before the fetch
   * even started. Checked first, same order `ReviewPage.tsx` uses, so a
   * customer sees this instead of an indefinite "Checking your session…".
   */
  if (query.fetchStatus === 'paused') {
    return (
      <div className={styles['gate']}>
        <p className={styles['mark']}>Pruvato</p>
        <SurfaceError>
          Could not reach the account server. Your sign-in is fine — this is on our side.
        </SurfaceError>
        <Button onClick={() => void query.refetch()}>Try again</Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className={styles['gate']}>
        <p className={styles['mark']}>Pruvato</p>
        <p className={styles['gateText']}>Checking your session…</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles['gate']}>
        <p className={styles['mark']}>Pruvato</p>
        <SurfaceError>
          Could not reach the account server. Your sign-in is fine — this is on our side. Try
          reloading in a moment.
        </SurfaceError>
        <Button onClick={() => void query.refetch()}>Try again</Button>
      </div>
    )
  }

  if (data === null) {
    return <Navigate to="/customer/login" replace state={{ from: location }} />
  }

  return (
    <div className={styles['shell']}>
      <header className={styles['header']}>
        <p className={styles['mark']}>Pruvato</p>
        <nav className={styles['nav']} aria-label="Account settings">
          {NAV.map((item) => {
            const current = location.pathname === item.to
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cx(styles['navLink'], current && styles['navLinkCurrent'])}
                aria-current={current ? 'page' : undefined}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>
        <span className={styles['identity']}>
          {data.user.name ?? data.user.email ?? 'Your account'}
          <span className={styles['role']}>{data.user.role}</span>
        </span>
        <Button
          type="button"
          loading={logout.isPending}
          onClick={() => {
            logout.mutate()
          }}
        >
          {logout.isPending ? 'Signing out…' : 'Sign out'}
        </Button>
      </header>
      <div className={styles['content']}>
        <Outlet context={data} />
      </div>
    </div>
  )
}
